import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import VoiceSelector from '../../common/VoiceSelector';

interface OrderingItem {
  id: string;
  text: string;
  correctPosition: number;
}

interface OrderingConfigProps {
  onConfigChange: (config: any) => void;
  initialConfig?: any;
}

const OrderingConfig: React.FC<OrderingConfigProps> = ({
  onConfigChange,
  initialConfig = {},
}) => {
  const [items, setItems] = useState<OrderingItem[]>(
    initialConfig.items || []
  );
  const [questionText, setQuestionText] = useState<string>(
    initialConfig.questionText || ''
  );
  const [hint, setHint] = useState<string>(initialConfig.hint || '');
  const [selectedVoiceCode, setSelectedVoiceCode] = useState<string>(
    initialConfig.voiceCode || 'fi-FI-NooraNeural'
  );
  const [enableTTS, setEnableTTS] = useState<boolean>(
    initialConfig.enableTTS ?? true
  );
  const [orientation, setOrientation] = useState<'vertical' | 'horizontal'>(
    initialConfig.orientation || 'vertical'
  );

  useEffect(() => {
    updateConfig();
  }, [items, questionText, hint, selectedVoiceCode, enableTTS, orientation]);

  const updateConfig = () => {
    onConfigChange({
      exerciseType: 'ordering',
      items,
      questionText,
      hint,
      voiceCode: selectedVoiceCode,
      enableTTS,
      orientation,
    });
  };

  const addItem = () => {
    const newItem: OrderingItem = {
      id: `item_${Date.now()}`,
      text: '',
      correctPosition: items.length,
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    const updatedItems = items
      .filter((item) => item.id !== id)
      .map((item, index) => ({
        ...item,
        correctPosition: index,
      }));
    setItems(updatedItems);
  };

  const updateItemText = (id: string, text: string) => {
    setItems(
      items.map((item) =>
        item.id === id ? { ...item, text } : item
      )
    );
  };

  const moveItemUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    // Update correctPosition for both items
    newItems.forEach((item, idx) => {
      item.correctPosition = idx;
    });
    setItems(newItems);
  };

  const moveItemDown = (index: number) => {
    if (index === items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    // Update correctPosition for both items
    newItems.forEach((item, idx) => {
      item.correctPosition = idx;
    });
    setItems(newItems);
  };

  const autoGenerateItems = () => {
    // Placeholder for AI generation - can be implemented later
    alert('AI-pohjainen generointi toteutetaan myöhemmin');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Järjestä oikeaan järjestykseen
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          label="Tehtävän kysymys"
          placeholder="Esim: Järjestä tapahtumat kronologiseen järjestykseen"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          margin="normal"
          multiline
          rows={2}
        />

        <TextField
          fullWidth
          label="Vihje (valinnainen)"
          placeholder="Esim: Ajattele tapahtumien aikajärjestystä"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          margin="normal"
          multiline
          rows={2}
        />

        <Box mt={2} mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={enableTTS}
                onChange={(e) => setEnableTTS(e.target.checked)}
              />
            }
            label="Ota käyttöön tekstistä puheeksi (TTS)"
          />
        </Box>

        <Box mt={2} mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Järjestyksen suunta
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant={orientation === 'vertical' ? 'contained' : 'outlined'}
              onClick={() => setOrientation('vertical')}
            >
              Pystysuora
            </Button>
            <Button
              variant={orientation === 'horizontal' ? 'contained' : 'outlined'}
              onClick={() => setOrientation('horizontal')}
            >
              Vaakasuora
            </Button>
          </Box>
        </Box>

        {enableTTS && (
          <Box mt={2} mb={2}>
            <VoiceSelector
              initialVoiceCode={selectedVoiceCode}
              onSelectVoice={(voice) => setSelectedVoiceCode(voice.code)}
            />
          </Box>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Kohdat oikeassa järjestyksessä ({items.length})
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={autoGenerateItems}
              sx={{ mr: 1 }}
            >
              Generoi AI:lla
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={addItem}
            >
              Lisää kohta
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="textSecondary" gutterBottom>
          Lisää kohdat oikeassa järjestyksessä. Opiskelija saa ne sekoitettuna.
        </Typography>

        {items.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Ei kohtia. Lisää vähintään 2 kohtaa.
          </Typography>
        ) : (
          <List>
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Box sx={{ mr: 2, fontWeight: 'bold', minWidth: 40 }}>
                    {index + 1}.
                  </Box>
                  <ListItemText
                    primary={
                      <TextField
                        fullWidth
                        placeholder={`Kohta ${index + 1}`}
                        value={item.text}
                        onChange={(e) => updateItemText(item.id, e.target.value)}
                        variant="outlined"
                        size="small"
                        multiline
                      />
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => moveItemUp(index)}
                      disabled={index === 0}
                      title="Siirrä ylös"
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveItemDown(index)}
                      disabled={index === items.length - 1}
                      title="Siirrä alas"
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeItem(item.id)}
                      title="Poista"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        {items.length > 0 && items.length < 2 && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Lisää vähintään 2 kohtaa järjestettäväksi.
          </Typography>
        )}
      </Paper>

      <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
        <Typography variant="body2" color="textSecondary">
          <strong>Huomio:</strong> Opiskelija saa kohdat satunnaisessa järjestyksessä ja hänen tulee järjestää ne oikeaan järjestykseen vetämällä ja pudottamalla.
        </Typography>
      </Paper>
    </Box>
  );
};

export default OrderingConfig;
